// App.js
import React from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { AuthProvider } from './contexts/AuthContext';
import { CompanyProvider } from './contexts/CompanyContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { NavigationGuardProvider } from './contexts/NavigationGuardContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import LoginPage from './components/auth/LoginPage';
import SetupProfilePage from './components/auth/SetupProfilePage';
import Dashboard from './components/Dashboard';
import ErrorBoundary from './components/shared/ErrorBoundary';
import OfflineIndicator from './components/OfflineIndicator';
import InstallBanner from './components/InstallBanner';
import theme from './theme/theme';

const App = () => {
  // PWA back-gesture guard: push a history entry so the user can't
  // accidentally navigate away from the app with a back swipe.
  // Without this, the first back gesture in a standalone PWA closes it.
  React.useEffect(() => {
    const isStandalone = typeof window.matchMedia === 'function'
      && window.matchMedia('(display-mode: standalone)').matches;
    if (!isStandalone) return;

    // Push a dummy entry so there's always a "back" page (this app itself)
    try {
      window.history.pushState(null, '', window.location.href);
    } catch { return; }
    const handlePopState = () => {
      // Re-push so the user stays in the app
      try { window.history.pushState(null, '', window.location.href); } catch { /* noop */ }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  return (
    <BrowserRouter>
      <ThemeProvider theme={theme}>
        {/* SettingsProvider outside Auth so it loads in parallel — no auth dependency */}
        <SettingsProvider>
        <AuthProvider>
          <CompanyProvider>
            <NavigationGuardProvider>
              <ErrorBoundary>
              {/* PWA: Offline indicator and install banner */}
              <OfflineIndicator />
              <InstallBanner />
              <Routes>
                {/* Public Routes */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/setup-profile" element={<SetupProfilePage />} />

                {/* Protected Routes - Entry point for the main application */}
                <Route
                  path="/*"
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
              </Routes>
              </ErrorBoundary>
            </NavigationGuardProvider>
          </CompanyProvider>
        </AuthProvider>
        </SettingsProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
};

export default App;
